import React from 'react';
import styled from 'styled-components';
import { CrudStyles } from '../../shared/Crud';
import { formatCurrencyBR, formatDateBR } from '../../../utils/masks';

const { SectionTitle, TableContainer, Table } = CrudStyles;

const DIMENSIONS_TOOLTIP = 'Valores exibidos na ordem: Altura × Largura × Comprimento (em centímetros).';

const SectionBox = styled.div`
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 20px;
    background: ${({ theme }) => theme?.colors?.gray?.[50] || '#f8f9fa'};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    border: 1px solid ${({ theme }) => theme?.colors?.gray?.[100] || 'rgba(0,0,0,0.06)'};
`;

const SectionTitleSoft = styled(SectionTitle)`
    font-size: 15px;
    font-weight: 600;
    color: ${({ theme }) => theme?.colors?.gray?.[800] || '#374151'};
    margin-bottom: 12px;
    margin-top: 0;
`;

const InfoHint = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    margin-left: 4px;
    border-radius: 50%;
    background: ${({ theme }) => theme?.colors?.gray?.[300] || '#d1d5db'};
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    cursor: help;
    vertical-align: middle;
`;

const AddressBlock = styled.pre`
    margin: 0 0 12px;
    white-space: pre-wrap;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.5;
    color: ${({ theme }) => theme?.colors?.gray?.[600] || '#4b5563'};
`;

const ReadOnlyLine = styled.p`
    margin: 0 0 8px;
    font-size: 14px;
    color: ${({ theme }) => theme?.colors?.gray?.[700] || '#374151'};
`;

const ValuesBox = styled(SectionBox)`
    background: ${({ theme }) => theme?.colors?.primary?.light ? `${theme.colors.primary.light}08` : 'rgba(59, 130, 246, 0.04)'};
    border-color: ${({ theme }) => theme?.colors?.primary?.light ? `${theme.colors.primary.light}40` : 'rgba(59, 130, 246, 0.12)'};
`;

/** Altura aproximada de uma linha (padding 16px + texto ~21px). */
const ITEM_ROW_HEIGHT_PX = 53;
/** Cabeçalho + até 3 itens visíveis sem rolagem. */
const ITEMS_SCROLL_MAX_HEIGHT_PX = ITEM_ROW_HEIGHT_PX * 4;

const ItemsTableScroll = styled(TableContainer)`
    margin-bottom: 0;
    max-height: ${ITEMS_SCROLL_MAX_HEIGHT_PX}px;
    overflow: auto;
    -webkit-overflow-scrolling: touch;

    thead th {
        position: sticky;
        top: 0;
        z-index: 1;
        box-shadow: 0 1px 0 ${({ theme }) => theme?.colors?.gray?.[100] || '#eee'};
    }
`;

const FeeRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid #e5e7eb;
    font-size: 14px;
    color: #374151;
    &:last-of-type {
        border-bottom: none;
    }
`;

const TotalRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 14px 0 0;
    margin-top: 12px;
    border-top: 2px solid ${({ theme }) => theme?.colors?.gray?.[300] || '#d1d5db'};
    font-size: 16px;
    color: ${({ theme }) => theme?.colors?.gray?.[900] || '#111'};
`;

function formatAddress(addr) {
    if (!addr || (!addr.street && !addr.city)) return '—';
    const parts = [
        [addr.street, addr.number, addr.complement].filter(Boolean).join(', '),
        [addr.district, addr.city, addr.stateAbbr].filter(Boolean).join(' — '),
        addr.zipCode,
    ].filter(Boolean);
    return parts.join('\n') || '—';
}

function splitDateTime(dateTimeStr) {
    if (!dateTimeStr || typeof dateTimeStr !== 'string') return { date: '', time: '' };
    const trimmed = dateTimeStr.trim();
    if (trimmed.length >= 16 && trimmed[10] === 'T') {
        return { date: trimmed.slice(0, 10), time: trimmed.slice(11, 16) };
    }
    if (trimmed.length >= 10) return { date: trimmed.slice(0, 10), time: '' };
    return { date: trimmed, time: '' };
}

function formatDateTimeDisplay(dateStr, timeStr, showTime) {
    const d = formatDateBR(dateStr);
    if (!d) return '—';
    if (showTime && timeStr) return `${d} às ${timeStr}`;
    return d;
}

export default function RevisaoStep({
    pickupAddress,
    deliveryAddress,
    items = [],
    pickupDateTime = '',
    pickupInstructions = '',
    deliveryDateTime = '',
    deliveryInstructions = '',
    deliveryValue,
    deliveryBillDescription = '',
    fees = [],
    recipientName = '',
    recipientDocument = '',
    recipientPhone = '',
    driverId = null,
    driverName = '',
    isTransportCompany,
    showDriverSection = true,
    showValuesSection = true,
}) {
    const pickup = splitDateTime(pickupDateTime);
    const delivery = splitDateTime(deliveryDateTime);
    const showTime = isTransportCompany;

    return (
        <>
            <SectionBox>
                <SectionTitleSoft>Dados da coleta</SectionTitleSoft>
                <AddressBlock>{formatAddress(pickupAddress)}</AddressBlock>
                <ReadOnlyLine>
                    <strong>Data{showTime ? ' e hora' : ''}:</strong>{' '}
                    {formatDateTimeDisplay(pickup.date, pickup.time, showTime)}
                </ReadOnlyLine>
                {pickupInstructions ? (
                    <ReadOnlyLine>
                        <strong>Instruções de coleta:</strong> {pickupInstructions}
                    </ReadOnlyLine>
                ) : null}
            </SectionBox>

            <SectionBox>
                <SectionTitleSoft>Dados da entrega</SectionTitleSoft>
                <AddressBlock>{formatAddress(deliveryAddress)}</AddressBlock>
                <ReadOnlyLine>
                    <strong>Data{showTime ? ' e hora' : ''}:</strong>{' '}
                    {formatDateTimeDisplay(delivery.date, delivery.time, showTime)}
                </ReadOnlyLine>
                {deliveryInstructions ? (
                    <ReadOnlyLine>
                        <strong>Instruções de entrega:</strong> {deliveryInstructions}
                    </ReadOnlyLine>
                ) : null}
            </SectionBox>

            {(recipientName || recipientDocument || recipientPhone) ? (
                <SectionBox>
                    <SectionTitleSoft>Responsável pelo recebimento</SectionTitleSoft>
                    {recipientName ? <ReadOnlyLine><strong>Nome:</strong> {recipientName}</ReadOnlyLine> : null}
                    {recipientDocument ? <ReadOnlyLine><strong>Documento (CPF):</strong> {recipientDocument}</ReadOnlyLine> : null}
                    {recipientPhone ? <ReadOnlyLine><strong>Telefone:</strong> {recipientPhone}</ReadOnlyLine> : null}
                </SectionBox>
            ) : null}

            {isTransportCompany && showDriverSection ? (
                <SectionBox>
                    <SectionTitleSoft>Motorista da entrega</SectionTitleSoft>
                    <ReadOnlyLine>
                        <strong>Motorista:</strong>{' '}
                        {driverId != null ? driverName?.trim() || `ID ${driverId}` : 'Não atribuído'}
                    </ReadOnlyLine>
                </SectionBox>
            ) : null}

            <SectionBox>
                <SectionTitleSoft>Itens da entrega</SectionTitleSoft>
                {items?.length > 0 ? (
                    <ItemsTableScroll>
                        <Table>
                            <thead>
                                <tr>
                                    <th>Qtd</th>
                                    <th>Descrição</th>
                                    <th>
                                        Dimensões (cm)
                                        <InfoHint title={DIMENSIONS_TOOLTIP}>?</InfoHint>
                                    </th>
                                    <th>Peso (kg)</th>
                                    <th>Frágil</th>
                                    <th>Preço</th>
                                    <th>Doc/NF</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((item, i) => (
                                    <tr key={item.id || i}>
                                        <td>{item.quantity || '—'}</td>
                                        <td>{item.description || '—'}</td>
                                        <td>
                                            {[item.height, item.width, item.length].some(Boolean)
                                                ? [item.height, item.width, item.length].filter(Boolean).join(' x ') + ' cm'
                                                : '—'}
                                        </td>
                                        <td>{item.weight ? `${item.weight} kg` : '—'}</td>
                                        <td>{item.fragile ? 'Sim' : 'Não'}</td>
                                        <td>{item.price != null ? formatCurrencyBR(item.price) : '—'}</td>
                                        <td>{item.documentNumber || '—'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </ItemsTableScroll>
                ) : (
                    <p style={{ color: '#6b7280', margin: 0, fontSize: '14px' }}>Nenhum item adicionado.</p>
                )}
            </SectionBox>

            {showValuesSection && (
                <ValuesBox>
                    <SectionTitleSoft>Valores</SectionTitleSoft>
                    <ReadOnlyLine>
                        <strong>Valor da entrega:</strong>{' '}
                        {deliveryValue != null ? formatCurrencyBR(deliveryValue) : '—'}
                    </ReadOnlyLine>
                    {deliveryBillDescription ? (
                        <ReadOnlyLine>
                            <strong>Descrição da cobrança:</strong> {deliveryBillDescription}
                        </ReadOnlyLine>
                    ) : null}
                    {(() => {
                        const persistedFees = (fees || []).filter((f) => f.feeId != null);
                        if (persistedFees.length === 0) return null;
                        return (
                            <>
                                <ReadOnlyLine style={{ marginTop: '16px', marginBottom: '8px' }}>
                                    <strong>Taxas:</strong>
                                </ReadOnlyLine>
                                {persistedFees.map((fee, i) => {
                                    const deliveryReais = (deliveryValue != null && deliveryValue !== '') ? Number(deliveryValue) : 0;
                                    const isPct = !!fee.isPercentage;
                                    const feeTotal = isPct
                                        ? (deliveryReais * (fee.value ?? 0)) / 100
                                        : (fee.value ?? 0) * Math.max(1, fee.quantity ?? 0);
                                    return (
                                        <FeeRow key={fee.feeId ?? fee.id ?? i}>
                                            <span>{fee.description || 'Taxa'}</span>
                                            <span>{formatCurrencyBR(feeTotal)}</span>
                                        </FeeRow>
                                    );
                                })}
                            </>
                        );
                    })()}
                    {(() => {
                        const deliveryReais = (deliveryValue != null && deliveryValue !== '') ? Number(deliveryValue) : 0;
                        const persistedFees = (fees || []).filter((f) => f.feeId != null);
                        const total = deliveryReais + persistedFees.reduce((sum, fee) => {
                            const isPct = !!fee.isPercentage;
                            return sum + (isPct
                                ? (deliveryReais * (fee.value ?? 0)) / 100
                                : (fee.value ?? 0) * Math.max(1, fee.quantity ?? 0));
                        }, 0);
                        return (
                            <TotalRow>
                                <span><strong>Valor total</strong></span>
                                <span><strong>{formatCurrencyBR(total)}</strong></span>
                            </TotalRow>
                        );
                    })()}
                </ValuesBox>
            )}
        </>
    );
}
